// Centralized broker API helpers. Mirrors the admin/api layout.
import { authFetch } from '../../auth/token';

const API = process.env.REACT_APP_API_BASE_URL;

// authFetch has already refreshed and retried by the time a 401 reaches here.
const handleError = async (res) => {
    let body = '';
    try { body = await res.text(); } catch { /* ignore */ }
    let serverMessage = '';
    try { serverMessage = JSON.parse(body).message; } catch { /* not JSON */ }
    throw new Error(serverMessage || `${res.status} ${res.statusText}${body ? ` — ${body}` : ''}`);
};

async function asJson(res) {
    if (!res.ok) {
        return handleError(res);
    }
    return res.json();
}

const json = (method, url, body) => authFetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
}).then(asJson);

const empty = (method, url, body) => authFetch(url, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: body !== undefined ? JSON.stringify(body) : undefined,
}).then(async (res) => {
    if (!res.ok) {
        return handleError(res);
    }
    return res;
});

// ----- Certs -----
export const Certs = {
    // Certs owned by the logged-in broker, resolved from the JWT via BrokersCerts.
    // Mirrors the admin /certs call: paginated and image-filtered so the cards
    // actually come back with their cert_images populated.
    listMine: ({ page = 1, count = 50, hasImages = true } = {}) => {
        const p = new URLSearchParams({ page, count });
        if (hasImages) p.set('hasImages', 'true');
        return json('GET', `${API}/certs/mine?${p}`);
    },
    // One cert by number, with its card and PriceCharting pricing once they exist.
    getByNumber: (certNumber) =>
        json('GET', `${API}/certs?${new URLSearchParams({ certNumber })}`),
    create: ({ certNumber }) =>
        json('POST', `${API}/cert`, {
            cert_number: certNumber,
        }),
    deactivate: (certNumber) =>
        empty('DELETE', `${API}/certs/deactivate?certNumber=${encodeURIComponent(certNumber)}`),
};

// ----- Listings -----
export const Listings = {
    listForSeller: ({ sellerEmail, count = 1000 }) => {
        const params = new URLSearchParams({ sellerEmail, count });
        return json('GET', `${API}/listings?${params}`);
    },
    // Resolves to { listings: [createdListing] }.
    create: ({ sellerEmail, certNumber, price, autoPrice = false }) =>
        json('POST', `${API}/listings`, {
            seller_email: sellerEmail,
            cert_number: certNumber,
            price: parseFloat(price),
            auto_price: autoPrice,
        }),
    // Resolves to the patched listing. Only the fields passed are sent, so a price
    // change leaves auto-price alone and vice versa.
    update: ({ listingId, price, autoPrice }) =>
        json('PATCH', `${API}/listings`, {
            listing_id: listingId,
            ...(price !== undefined && { price: parseFloat(price) }),
            ...(autoPrice !== undefined && { auto_price: autoPrice }),
        }),
    deactivate: (certNumber) =>
        empty('DELETE', `${API}/listings/deactivate?certNumber=${encodeURIComponent(certNumber)}`),
};

// ----- Orders -----
export const Orders = {
    // The API returns 12 orders when no count is given; ask for all of them.
    listForSeller: (sellerEmail) =>
        json('GET', `${API}/orders?sellerEmail=${encodeURIComponent(sellerEmail)}&count=1000`),
    confirm: (orderNumber) =>
        empty('POST', `${API}/confirmation`, {
            order_number: orderNumber,
            source: 'broker-dashboard',
        }),
    fulfill: ({ orderNumber, trackingNumber }) =>
        empty('POST', `${API}/fulfillment`, {
            order_number: orderNumber,
            source: 'broker-dashboard',
            tracking_number: trackingNumber || '',
        }),
};
